/**
 * Shared parser for accept-invite edge function responses.
 * Normalises the various shapes Supabase Functions client can return.
 */

type FunctionErrorShape = {
  context?: { data?: unknown; body?: unknown };
  data?: unknown;
  message?: unknown;
};

export interface AcceptInviteResult {
  ok: boolean;
  error?: string;
  message?: string;
  coachId?: string;
  leaderId?: string;
  onboardingComplete?: boolean;
  alreadyLinked?: boolean;
}

/**
 * Extracts a normalised result from the accept-invite edge function response.
 * Handles Supabase Functions client quirks where error info can appear in
 * data, error.context.data, error.context.body, or error.message.
 */
export function parseAcceptInviteResponse(
  data: Record<string, unknown> | null | undefined,
  error: unknown,
): AcceptInviteResult {
  // Happy path — data.ok is truthy
  if (data?.ok) {
    return {
      ok: true,
      coachId: data.coachId as string | undefined,
      leaderId: data.leaderId as string | undefined,
      onboardingComplete: data.onboardingComplete as boolean | undefined,
      alreadyLinked: data.alreadyLinked as boolean | undefined,
    };
  }

  // data exists but ok is false — error info is in data itself
  if (data && data.ok === false) {
    return {
      ok: false,
      error: (data.error as string) ?? "unknown",
      message: (data.message as string) ?? "We couldn't accept this invite. Please request a new link.",
    };
  }

  // No data — try to extract from the error object
  if (error) {
    const errorObj = error as FunctionErrorShape;
    const nested = (errorObj?.context?.data ||
      errorObj?.context?.body ||
      errorObj?.data ||
      (typeof errorObj?.message === "object" ? errorObj.message : null)) as
      | { error?: string; message?: string; ok?: boolean }
      | null;

    if (nested?.error) {
      return {
        ok: false,
        error: nested.error,
        message: nested.message ?? "We couldn't accept this invite. Please request a new link.",
      };
    }

    // Plain error message
    const msg =
      typeof errorObj?.message === "string"
        ? errorObj.message
        : "We couldn't accept this invite. Please request a new link.";

    return { ok: false, error: "unknown", message: msg };
  }

  return {
    ok: false,
    error: "unknown",
    message: "We couldn't accept this invite. Please request a new link.",
  };
}

/** Returns true when the error indicates an auth/timing issue worth retrying. */
export function isRetryableInviteError(result: AcceptInviteResult, rawError: unknown): boolean {
  const msg = (rawError as { message?: string })?.message ?? "";
  return (
    msg.includes("401") ||
    msg.includes("Invalid authorization") ||
    msg.includes("JWT") ||
    result.error === "invalid_token"
  );
}
